import { c as _c } from "react/compiler-runtime";
import { type CountryRecord, DEFAULT_COUNTRY } from '@onefootprint/core';
import type { Iso3166TwoDigitCountryCode } from '@onefootprint/request-types';
import { useEffect, useState } from 'react';
import useDecryptUser from '@/idv2/features/collect-document/hooks/use-decrypt-user';
import type { IdDocOutcome, SupportedIdDocType } from '@/idv2/features/collect-document/types';
import submitFingerprint from '@/idv2/features/collect-document/utils/submit-fingerprint';
import type { DeviceInfo } from '@/idv2/utils/get-device';
import { isObject } from '@/idv2/utils/type-guards';
import IdDocCountryAndTypeContainer from './components/id-doc-country-and-type-container';
import Loading from './components/loading';
type SubmitDocTypeResponse = {
  id: string;
};
type IdDocCountryAndTypeProps = {
  authToken: string;
  selectedCountryCode?: Iso3166TwoDigitCountryCode;
  onSubmitDocType: (data: {
    type: `${SupportedIdDocType}`;
    country: Iso3166TwoDigitCountryCode;
    id: string;
  }) => void;
  device: DeviceInfo;
  supportedCountryAndDocTypes: Partial<Record<Iso3166TwoDigitCountryCode, SupportedIdDocType[]>>;
  sandboxOutcome?: IdDocOutcome;
  selectedDocType?: `${SupportedIdDocType}`;
  documentRequestId: string;
  onContinueOnMobile?: () => void;
};
const IdDocCountryAndType = t0 => {
  const $ = _c(24);
  const {
    authToken,
    device,
    supportedCountryAndDocTypes,
    sandboxOutcome,
    selectedDocType,
    documentRequestId,
    onSubmitDocType,
    selectedCountryCode,
    onContinueOnMobile
  } = t0;
  const mutDecryptUser = useDecryptUser();
  const [userCountryCode, setUserCountryCode] = useState();
  let t1;
  if ($[0] !== supportedCountryAndDocTypes) {
    t1 = isObject(supportedCountryAndDocTypes) && Object.keys(supportedCountryAndDocTypes).length === 1;
    $[0] = supportedCountryAndDocTypes;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const hasOneSupportedCountry = t1;
  const needToDecrypt = authToken && !hasOneSupportedCountry && !selectedCountryCode && !userCountryCode;
  const decryptionCompleted = mutDecryptUser.isSuccess || mutDecryptUser.isError;
  let t2;
  if ($[2] !== authToken || $[3] !== onSubmitDocType) {
    t2 = (data, country, docType) => {
      const {
        id
      } = data;
      submitFingerprint({
        authToken,
        path: "identity_document_start",
        documentId: id
      });
      onSubmitDocType({
        type: docType,
        country: country.value ?? DEFAULT_COUNTRY.value,
        id
      });
    };
    $[2] = authToken;
    $[3] = onSubmitDocType;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleSubmitDocTypeSuccess = t2;
  let t3;
  if ($[5] !== authToken || $[6] !== mutDecryptUser || $[7] !== needToDecrypt) {
    t3 = () => {
      if (!needToDecrypt) {
        return;
      }
      if (mutDecryptUser.isIdle && !mutDecryptUser.isPending) {
        mutDecryptUser.mutate({
          authToken,
          fields: ["id.country"]
        }, {
          onError: console.warn,
          onSuccess: res => {
            const countryCode = res["id.country"] as Iso3166TwoDigitCountryCode | undefined;
            if (countryCode) {
              setUserCountryCode(countryCode);
            }
          }
        });
      }
    };
    $[5] = authToken;
    $[6] = mutDecryptUser;
    $[7] = needToDecrypt;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== mutDecryptUser.isIdle || $[10] !== mutDecryptUser.isPending || $[11] !== needToDecrypt) {
    t4 = [needToDecrypt, mutDecryptUser.isIdle, mutDecryptUser.isPending];
    $[9] = mutDecryptUser.isIdle;
    $[10] = mutDecryptUser.isPending;
    $[11] = needToDecrypt;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  useEffect(t3, t4);
  if (needToDecrypt && !decryptionCompleted) {
    let t5;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Loading />;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    return t5;
  }
  let t5;
  if ($[14] !== authToken || $[15] !== device || $[16] !== documentRequestId || $[17] !== handleSubmitDocTypeSuccess || $[18] !== onContinueOnMobile || $[19] !== sandboxOutcome || $[20] !== selectedDocType || $[21] !== supportedCountryAndDocTypes || $[22] !== userCountryCode) {
    t5 = <IdDocCountryAndTypeContainer onSubmitDocTypeSuccess={handleSubmitDocTypeSuccess} userCountryCode={userCountryCode} authToken={authToken} device={device} supportedCountryAndDocTypes={supportedCountryAndDocTypes} sandboxOutcome={sandboxOutcome} selectedDocType={selectedDocType} documentRequestId={documentRequestId} onContinueOnMobile={onContinueOnMobile} />;
    $[14] = authToken;
    $[15] = device;
    $[16] = documentRequestId;
    $[17] = handleSubmitDocTypeSuccess;
    $[18] = onContinueOnMobile;
    $[19] = sandboxOutcome;
    $[20] = selectedDocType;
    $[21] = supportedCountryAndDocTypes;
    $[22] = userCountryCode;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  return t5;
};
export default IdDocCountryAndType;